<script lang="ts">
import type { ToastRootEmits, ToastRootProps } from 'reka-ui'
import type { Component } from 'vue'
import type { StringOrVNode } from '../types/utils'
import type { ButtonProps } from './AwButton.vue'

export interface ToastProps extends Pick<ToastRootProps, 'defaultOpen' | 'open' | 'type' | 'duration'> {
  as?: string | Component
  text?: StringOrVNode
  color?: 'secondary' | 'success' | 'warning' | 'critical' | 'info'
  icon?: string
  actions?: ButtonProps[]
  isClosable?: boolean
  class?: string | object
}

export interface ToastEmits extends ToastRootEmits {}

const ui = {
  root: 'relative group overflow-hidden shadow-lg rounded-lg p-4.5 flex flex-col gap-3 bg-secondary-900 w-full',
  wrapper: 'flex gap-3 items-center',
  title: 'flex-1 text-sm font-medium text-white',
  actions: 'flex gap-2 shrink-0 justify-end',
  close: 'p-0 ml-auto cursor-pointer text-white size-4 min-w-4',
  icon: {
    secondary: 'text-white',
    success: 'text-success-400',
    warning: 'text-warning-400',
    critical: 'text-critical-400',
    info: 'text-info-400',
  },
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core'
import { ToastAction, ToastClose, ToastRoot, ToastTitle, useForwardPropsEmits } from 'reka-ui'
import { computed, nextTick, onMounted, ref } from 'vue'
import icons from '../styles/icons'
import AwButton from './AwButton.vue'
import AwIcon from './AwIcon.vue'

const props = withDefaults(defineProps<ToastProps>(), {
  isClosable: true,
})
const emits = defineEmits<ToastEmits>()

const rootProps = useForwardPropsEmits(reactivePick(props, 'as', 'defaultOpen', 'open', 'duration', 'type'), emits)

const el = ref()
const height = ref(0)

const rootClass = computed(() => [ui.root, props.class])

const iconClass = computed(() => ui.icon[props.color ?? 'secondary'])

onMounted(() => {
  if (!el.value) {
    return
  }
  nextTick(() => {
    height.value = el.value?.$el?.getBoundingClientRect()?.height
  })
})

defineExpose({ height })
</script>

<template>
  <ToastRoot
    ref="el"
    v-bind="rootProps"
    data-orientation="vertical"
    :class="rootClass"
    :style="{ '--height': height }"
  >
    <div :class="ui.wrapper">
      <AwIcon v-if="icon" :name="icon" class="size-4" :class="iconClass" />
      <ToastTitle v-if="text" :class="ui.title">
        <component :is="text()" v-if="typeof text === 'function'" />
        <component :is="text" v-else-if="typeof text === 'object'" />
        <template v-else>
          {{ text }}
        </template>
      </ToastTitle>
      <ToastClose v-if="isClosable" as-child>
        <AwIcon :name="icons.closeBold" :class="ui.close" />
      </ToastClose>
    </div>
    <div v-if="actions?.length" :class="ui.actions">
      <ToastAction v-for="(action, index) in actions" :key="index" :alt-text="action.label || 'Action'" as-child @click.stop>
        <AwButton size="sm" :color="action.color" v-bind="action" />
      </ToastAction>
    </div>
  </ToastRoot>
</template>
